import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Printer, ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getBillById, getSettings, editPayment, deletePayment } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { amountToWords } from "@/lib/amountWords";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/09a62dc9-6a9e-4bad-b528-41b496ab2382/images/cbcacb73a9857c56eaa830e6342d9e2d9c033cf8fd7db1c24131862abae98730.png";
const MODES = ["Cash", "UPI", "Cheque", "Bank Transfer", "Card", "NEFT"];

export default function PaymentReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth() || {};
  const [bill, setBill] = useState(null);
  const [settings, setSettings] = useState({});
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteIdx, setDeleteIdx] = useState(null);

  const canEdit = hasRole?.(["admin", "accountant"]);
  const canDelete = hasRole?.("admin");

  const reload = async () => { setBill(await getBillById(id)); };
  useEffect(() => { getBillById(id).then(setBill); getSettings().then(setSettings); }, [id]);

  const payments = bill?.payments || [];
  // Fallback: if no payments array, show legacy paymentDetails
  const displayPayments = payments.length > 0 ? payments : (bill?.paymentDetails ? [bill.paymentDetails] : []);

  const openEdit = (idx) => {
    const p = displayPayments[idx];
    setEditIdx(idx);
    setEditForm({
      amountReceived: p.amountReceived || 0,
      paymentMode: p.paymentMode || "Cash",
      referenceNo: p.referenceNo || "",
      paymentDate: p.paymentDate || "",
      notes: p.notes || "",
    });
  };

  const handleEditSave = async () => {
    try {
      await editPayment(bill.id, editIdx, editForm);
      await reload();
      setEditIdx(null);
      toast.success("Payment updated. Balances recalculated.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update payment"); }
  };

  const handleDelete = async () => {
    try {
      await deletePayment(bill.id, deleteIdx);
      await reload();
      setDeleteIdx(null);
      toast.success("Payment deleted. Balances recalculated.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete payment"); }
  };

  if (!bill) {
    return (
      <div className="p-8 text-center text-zinc-400" data-testid="receipt-not-found">
        <p>Receipt not found or payment not recorded.</p>
        <Button onClick={() => navigate(-1)} variant="link" className="mt-4">Go Back</Button>
      </div>
    );
  }

  if (displayPayments.length === 0) {
    return (
      <div className="p-8 text-center text-zinc-400" data-testid="receipt-no-payments">
        <p>No payments recorded for this bill.</p>
        <Button onClick={() => navigate(-1)} variant="link" className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="payment-receipt-page">
      <div className="no-print flex items-center gap-4 mb-6 max-w-[800px] mx-auto">
        <Button data-testid="receipt-back-button" variant="outline" onClick={() => navigate(-1)} className="rounded-sm border-zinc-300">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button data-testid="receipt-print-button" onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
          <Printer className="w-4 h-4 mr-2" /> Print Receipt
        </Button>
      </div>

      {/* Print-friendly receipt for each payment */}
      {displayPayments.map((pd, idx) => (
        <div key={pd.receiptNo || idx} className="print-root printable-invoice max-w-[800px] mx-auto bg-white border border-zinc-300 p-8 font-mono text-sm mb-6 print-page-break" data-testid={`receipt-${idx}`}>
          {/* Header */}
          <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
            <div className="flex items-start gap-3">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-10 object-contain" />
              <div>
                <h2 className="text-lg font-black font-heading tracking-tight">{settings.companyName || "SUPERFLAME GAS SERVICES"}</h2>
                <p className="text-[10px] text-zinc-600 mt-1">{settings.address}</p>
                <p className="text-[10px] text-zinc-600">Phone: {settings.phone}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-black font-heading text-emerald-700">PAYMENT RECEIPT</p>
              <p className="text-xs text-zinc-500 mt-1">Original</p>
            </div>
          </div>

          {/* Receipt Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Receipt No</p>
              <p className="font-semibold text-base">{pd.receiptNo || "—"}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Receipt Date</p>
              <p className="font-semibold">{pd.paymentDate ? new Date(pd.paymentDate).toLocaleDateString("en-IN") : "—"}</p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="border border-black p-4 mb-6 bg-zinc-50">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-3">Received From</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <p><span className="text-zinc-500">Name:</span> <span className="font-semibold">{bill.customerName}</span></p>
              <p><span className="text-zinc-500">Consumer No:</span> <span className="font-semibold">{bill.consumerNumber}</span></p>
              <p><span className="text-zinc-500">Society:</span> {bill.society}</p>
              <p><span className="text-zinc-500">Flat/Plot:</span> {bill.flatNo || bill.plotNo}</p>
            </div>
          </div>

          {/* Payment Details Table */}
          <table className="w-full border-collapse border border-black mb-6 text-xs">
            <thead><tr className="bg-zinc-100">
              <th className="border border-black p-2 text-left font-bold uppercase tracking-wider">Description</th>
              <th className="border border-black p-2 text-right font-bold uppercase tracking-wider">Details</th>
            </tr></thead>
            <tbody>
              <tr><td className="border border-black p-2">Invoice No</td><td className="border border-black p-2 text-right font-semibold">{bill.invoiceNo}</td></tr>
              <tr><td className="border border-black p-2">Bill Amount</td><td className="border border-black p-2 text-right font-semibold">₹{(bill.netPayable || 0).toFixed(2)}</td></tr>
              <tr><td className="border border-black p-2">Mode of Payment</td><td className="border border-black p-2 text-right font-semibold">{pd.paymentMode}</td></tr>
              {pd.referenceNo && <tr><td className="border border-black p-2">Reference / Txn ID</td><td className="border border-black p-2 text-right font-semibold">{pd.referenceNo}</td></tr>}
              <tr className="bg-emerald-50 font-bold">
                <td className="border border-black p-2 text-base">AMOUNT RECEIVED</td>
                <td className="border border-black p-2 text-right text-base text-emerald-700">₹{(pd.amountReceived || 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <p className="text-[10px] italic text-zinc-500 mb-4 -mt-4">{amountToWords(pd.amountReceived || 0)}</p>

          {pd.notes && <p className="mb-4 text-xs text-zinc-500">Notes: {pd.notes}</p>}

          {/* Edit history */}
          {pd.editHistory && pd.editHistory.length > 0 && (
            <div className="mb-4 text-[10px] text-zinc-400 border-t border-zinc-200 pt-2">
              {pd.editHistory.map((eh, eIdx) => (
                <p key={eIdx}>Edited by {eh.editedBy} on {new Date(eh.editedAt).toLocaleString("en-IN")}</p>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-black pt-4">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-[10px] text-zinc-500">Computer generated receipt.</p>
                <p className="text-[10px] text-zinc-500">For queries: {settings.phone}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-sm">FOR {settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
                <p className="text-[10px] text-zinc-500 mt-6">Authorized Signatory</p>
              </div>
            </div>
          </div>

          {/* Edit/Delete buttons (screen only) */}
          {canEdit && (
            <div className="no-print mt-4 pt-3 border-t border-zinc-200 flex gap-2">
              <Button data-testid={`edit-payment-${idx}`} variant="outline" size="sm" onClick={() => openEdit(idx)} className="rounded-sm text-xs">
                <Pencil className="w-3 h-3 mr-1" /> Edit Receipt
              </Button>
              {canDelete && (
                <Button data-testid={`delete-payment-${idx}`} variant="outline" size="sm" onClick={() => setDeleteIdx(idx)} className="rounded-sm text-xs text-red-500 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
              {pd.recordedBy && <span className="text-[10px] text-zinc-400 self-center ml-2">Recorded by {pd.recordedBy}{pd.recordedAt ? ` on ${new Date(pd.recordedAt).toLocaleString("en-IN")}` : ""}</span>}
            </div>
          )}
        </div>
      ))}

      {/* Payment History Summary (screen only, for bills with multiple payments) */}
      {displayPayments.length > 1 && (
        <div className="no-print max-w-[800px] mx-auto mt-4 mb-8">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">All Payments for Invoice {bill.invoiceNo}</p>
          <Table>
            <TableHeader><TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-bold uppercase text-zinc-500">#</TableHead>
              <TableHead className="text-xs font-bold uppercase text-zinc-500">Date</TableHead>
              <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Amount</TableHead>
              <TableHead className="text-xs font-bold uppercase text-zinc-500">Mode</TableHead>
              <TableHead className="text-xs font-bold uppercase text-zinc-500">Receipt</TableHead>
              <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Balance</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(() => {
                let bal = bill.netPayable || 0;
                return displayPayments.map((p, i) => {
                  bal -= p.amountReceived || 0;
                  return (
                    <TableRow key={p.receiptNo || i}>
                      <TableCell className="text-xs">{i + 1}</TableCell>
                      <TableCell className="text-xs">{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString("en-IN") : "—"}</TableCell>
                      <TableCell className="text-xs font-mono text-right text-emerald-600">₹{(p.amountReceived || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs">{p.paymentMode}</TableCell>
                      <TableCell className="text-xs font-mono">{p.receiptNo || "—"}</TableCell>
                      <TableCell className={`text-xs font-mono text-right font-semibold ${bal > 0 ? "text-red-600" : bal < 0 ? "text-emerald-600" : ""}`}>
                        {bal > 0 ? `₹${bal.toFixed(2)} due` : bal < 0 ? `Adv ₹${Math.abs(bal).toFixed(2)}` : "Settled"}
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Payment Dialog */}
      <Dialog open={editIdx !== null} onOpenChange={(o) => { if (!o) setEditIdx(null); }}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Edit Payment</DialogTitle>
            <DialogDescription>Update receipt details. Balances will be recalculated.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Amount Received</Label><Input data-testid="edit-receipt-amount" type="number" step="0.01" value={editForm.amountReceived || 0} onChange={(e) => setEditForm({ ...editForm, amountReceived: parseFloat(e.target.value) || 0 })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Mode of Payment</Label>
              <Select value={editForm.paymentMode || "Cash"} onValueChange={(v) => setEditForm({ ...editForm, paymentMode: v })}>
                <SelectTrigger data-testid="edit-receipt-mode" className="bg-zinc-50 border-zinc-300 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Reference / Txn ID</Label><Input data-testid="edit-receipt-ref" value={editForm.referenceNo || ""} onChange={(e) => setEditForm({ ...editForm, referenceNo: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Payment Date</Label><Input data-testid="edit-receipt-date" type="date" value={editForm.paymentDate || ""} onChange={(e) => setEditForm({ ...editForm, paymentDate: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Notes</Label><Input data-testid="edit-receipt-notes" value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="save-edit-receipt" onClick={handleEditSave} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteIdx !== null} onOpenChange={(o) => { if (!o) setDeleteIdx(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the payment record and recalculate all balances. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-payment" onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">Delete Payment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
